import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import {
  Plus, Edit, Trash2, Search, UserCheck, UserX, Loader2, KeyRound,
  Shield, Clock, X, Check, Copy, Sparkles, LogOut,
} from 'lucide-react';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Spinner } from '../../components/ui/Spinner';
import {
  useGetUtilisateursQuery,
  useCreerUtilisateurMutation,
  useModifierUtilisateurMutation,
  useSupprimerUtilisateurMutation,
  useResetPasswordMutation,
  useAffecterPolesUtilisateurMutation,
  useAccorderAccesTemporaireMutation,
  useRevoquerAccesTemporaireMutation,
  useEnseignantsDisponiblesCompteQuery,
  useDeconnecterUnMutation,
} from '../../services/coreApi';
import type { EnseignantDisponibleItem } from '../../services/coreApi';
import SearchableSelect from '../../components/SearchableSelect';
import { useGetPolesQuery } from '../../services/polesOptionsApi';
import type { UtilisateurList, UtilisateurCreate, UtilisateurUpdate } from '../../services/coreApi';

const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super Admin',
  admin: 'Administrateur',
  agent: 'Agent Scolarité',
  agent_caisse: 'Agent de Caisse',
  directeur: 'Directeur (lecture seule)',
  surveillant: 'Surveillant',
  formateur: 'Formateur / Enseignant',
  marketing: 'Marketing',
  bureau_stages: 'Bureau des Stages',
  consultation: 'Consultation',
  coordonnateur: 'Coordonnateur pédagogique',
  invite: 'Invité (aide examen)',
};

const ROLE_BADGE: Record<string, 'danger' | 'warning' | 'info' | 'default'> = {
  super_admin: 'danger',
  admin: 'warning',
  agent: 'info',
  agent_caisse: 'info',
  directeur: 'default',
  surveillant: 'info',
  formateur: 'info',
  marketing: 'default',
  bureau_stages: 'default',
  consultation: 'default',
  coordonnateur: 'info',
  invite: 'default',
};

const EMPTY_CREATE: UtilisateurCreate = {
  username: '', email: '', nom: '', prenoms: '', role: 'agent', password: '', password2: '', poles_autorises: [],
  enseignant_id_lien: null,
};

/** Génère un username depuis prénom + nom : "Jean Dupont" → "jdupont" */
function suggererUsername(nom: string, prenoms: string): string {
  const slug = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z]/g, '');
  const p = slug(prenoms.split(' ')[0] || '').slice(0, 1);
  return (p + slug(nom)).slice(0, 30) || 'user';
}

const UtilisateursTab: React.FC = () => {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const { data: utilisateurs, isLoading } = useGetUtilisateursQuery();
  const [creer, { isLoading: creating }] = useCreerUtilisateurMutation();
  const [modifier, { isLoading: updating }] = useModifierUtilisateurMutation();
  const [supprimer] = useSupprimerUtilisateurMutation();
  const [resetPassword] = useResetPasswordMutation();
  // Fix 2026-09-09 : déconnexion ciblée d'un utilisateur
  const [deconnecterUn, { isLoading: deconnecting }] = useDeconnecterUnMutation();
  const [affecterPoles, { isLoading: affectingPoles }] = useAffecterPolesUtilisateurMutation();
  const [accorderAcces] = useAccorderAccesTemporaireMutation();
  const [revoquerAcces] = useRevoquerAccesTemporaireMutation();
  const { data: poles = [] } = useGetPolesQuery({ actif: true });

  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<UtilisateurList | null>(null);
  const [createForm, setCreateForm] = useState<UtilisateurCreate>(EMPTY_CREATE);
  const [editForm, setEditForm] = useState<UtilisateurUpdate>({});
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [resetUserId, setResetUserId] = useState<number | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [polesUserId, setPolesUserId] = useState<number | null>(null);
  const [polesSelected, setPolesSelected] = useState<string[]>([]);
  const [tempAccesPole, setTempAccesPole] = useState('');
  const [tempAccesDate, setTempAccesDate] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  // Mdp généré à afficher post-création (une seule fois)
  const [mdpGenere, setMdpGenere] = useState<{
    username: string; nomComplet: string; password: string;
  } | null>(null);
  const [mdpCopie, setMdpCopie] = useState(false);
  // Pour le SearchableSelect enseignant (uniquement quand role=formateur)
  const { data: enseignantsData } = useEnseignantsDisponiblesCompteQuery(undefined, {
    skip: createForm.role !== 'formateur' || editing !== null,
  });
  const enseignantsDisponibles: EnseignantDisponibleItem[] =
    (enseignantsData?.results || []).filter(e => !e.a_compte);
  const enseignantSelectionne = enseignantsDisponibles.find(
    e => e.id === createForm.enseignant_id_lien
  ) || null;

  const showMsg = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  const filtered = useMemo(() => {
    if (!utilisateurs) return [];
    if (!search.trim()) return utilisateurs;
    const s = search.toLowerCase();
    return utilisateurs.filter(u =>
      u.username.toLowerCase().includes(s) ||
      u.nom.toLowerCase().includes(s) ||
      u.prenoms.toLowerCase().includes(s) ||
      u.email.toLowerCase().includes(s)
    );
  }, [utilisateurs, search]);

  const openCreate = () => {
    setEditing(null);
    setCreateForm(EMPTY_CREATE);
    setShowForm(true);
  };

  const openEdit = (u: UtilisateurList) => {
    setEditing(u);
    setEditForm({ username: u.username, email: u.email, nom: u.nom, prenoms: u.prenoms, role: u.role, actif: u.actif });
    setShowForm(true);
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await modifier({ id: editing.id, data: editForm }).unwrap();
        showMsg('success', 'Utilisateur modifié');
        setShowForm(false);
      } else {
        // Si formateur via enseignant : pas besoin de password (généré côté serveur)
        const payload: UtilisateurCreate = { ...createForm };
        if (payload.role === 'formateur' && payload.enseignant_id_lien) {
          // Mdp serveur : on envoie vide
          payload.password = '';
          payload.password2 = '';
          // Username auto-suggéré si vide
          if (!payload.username) {
            payload.username = suggererUsername(payload.nom, payload.prenoms);
          }
        }
        const res = await creer(payload).unwrap();
        // Si le serveur a généré un mdp, on l'affiche
        if (res.generated_password) {
          setMdpGenere({
            username: res.username,
            nomComplet: `${res.nom || ''} ${res.prenoms || ''}`.trim(),
            password: res.generated_password,
          });
          setMdpCopie(false);
          // On ferme le formulaire mais on garde le modal mdp ouvert
          setShowForm(false);
        } else {
          showMsg('success', 'Utilisateur créé');
          setShowForm(false);
        }
      }
    } catch (err: any) {
      const detail = err.data?.detail || err.data?.username?.[0] || err.data?.email?.[0] || err.data?.password?.[0] || err.data?.enseignant_id_lien?.[0] || err.data?.non_field_errors?.[0] || 'Erreur';
      showMsg('error', detail);
    }
  };

  const copierMdp = async () => {
    if (!mdpGenere) return;
    try {
      await navigator.clipboard.writeText(
        `Identifiant : ${mdpGenere.username}\nMot de passe : ${mdpGenere.password}`
      );
      setMdpCopie(true);
      setTimeout(() => setMdpCopie(false), 2000);
    } catch {
      // ignore
    }
  };

  const handleToggleActif = async (u: UtilisateurList) => {
    if (u.id === currentUser?.id) return;
    try {
      await modifier({ id: u.id, data: { actif: !u.actif } }).unwrap();
      showMsg('success', u.actif ? 'Utilisateur désactivé' : 'Utilisateur activé');
    } catch {
      showMsg('error', 'Erreur lors de la modification');
    }
  };

  const handleDelete = async () => {
    if (deleteId === null) return;
    try {
      await supprimer(deleteId).unwrap();
      showMsg('success', 'Utilisateur supprimé');
      setDeleteId(null);
    } catch (err: any) {
      showMsg('error', err.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const formatDate = (d: string | null) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (isLoading) return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-5">
      {message && (
        <div className={`p-3 rounded-lg text-sm ${
          message.type === 'success'
            ? 'bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-700'
            : 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-700'
        }`}>
          {message.text}
        </div>
      )}

      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 max-w-sm relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher un utilisateur..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
          />
        </div>
        <Button variant="primary" icon={<Plus size={16} />} onClick={openCreate}>
          Nouvel utilisateur
        </Button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Nom complet</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Username</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Rôle</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Pôles autorisés</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Statut</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Dernière connexion</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filtered.map((u) => {
                const isSelf = u.id === currentUser?.id;
                return (
                  <tr key={u.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 font-medium">
                      {u.nom} {u.prenoms}
                      {isSelf && <span className="ml-2 text-xs text-blue-500">(vous)</span>}
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-gray-700 dark:text-gray-300">{u.username}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{u.email}</td>
                    <td className="px-4 py-3">
                      <Badge variant={ROLE_BADGE[u.role] || 'default'} size="sm">{ROLE_LABELS[u.role] || u.role}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      {u.role === 'super_admin' || u.role === 'admin' ? (
                        <span className="text-[10px] italic text-gray-400">Tous (admin)</span>
                      ) : (u.poles_autorises && u.poles_autorises.length > 0) ? (
                        <div className="flex flex-wrap gap-1">
                          {u.poles_autorises.map(p => (
                            <span key={p} className="text-[10px] px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">{p}</span>
                          ))}
                          {(u.poles_acces_temporaire || []).filter(a => new Date(a.date_expiration) >= new Date()).map(a => (
                            <span key={a.pole} className="text-[10px] px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded inline-flex items-center gap-0.5" title={`Expire le ${a.date_expiration}`}>
                              <Clock size={9} /> {a.pole}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-[10px] italic text-gray-400">Tous</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge variant={u.actif ? 'success' : 'danger'} size="sm">{u.actif ? 'Actif' : 'Inactif'}</Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{formatDate(u.derniere_connexion)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="outline" size="sm" icon={<Edit size={14} />} onClick={() => openEdit(u)} title="Modifier" />
                        <Button
                          variant="outline"
                          size="sm"
                          icon={u.actif ? <UserX size={14} /> : <UserCheck size={14} />}
                          onClick={() => handleToggleActif(u)}
                          disabled={isSelf}
                          title={u.actif ? 'Désactiver' : 'Activer'}
                          className={u.actif ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400'}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          icon={<Shield size={14} />}
                          onClick={() => {
                            setPolesUserId(u.id);
                            setPolesSelected(u.poles_autorises || []);
                            setTempAccesPole('');
                            setTempAccesDate('');
                          }}
                          className="text-blue-600 dark:text-blue-400"
                          title="Gérer les pôles autorisés"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          icon={<KeyRound size={14} />}
                          onClick={() => { setResetUserId(u.id); setNewPassword(''); }}
                          disabled={isSelf}
                          className="text-purple-600 dark:text-purple-400"
                          title="Réinitialiser le mot de passe"
                        />
                        {/* Fix 2026-09-09 : déconnexion ciblée */}
                        <Button
                          variant="outline"
                          size="sm"
                          icon={<LogOut size={14} />}
                          onClick={async () => {
                            const nom = `${u.prenoms || ''} ${u.nom || ''}`.trim() || u.username;
                            if (!window.confirm(
                              `Déconnecter ${nom} ?\n\n` +
                              `Ses tokens actuels seront invalidés. À sa prochaine action, ` +
                              `il devra se reconnecter.`
                            )) return;
                            try {
                              const r = await deconnecterUn(u.id).unwrap();
                              alert(r.message || `${nom} sera déconnecté à sa prochaine requête.`);
                            } catch (e: any) {
                              alert(e?.data?.error || e?.data?.detail || 'Erreur déconnexion');
                            }
                          }}
                          disabled={isSelf || deconnecting}
                          className="text-amber-600 dark:text-amber-400"
                          title={isSelf
                            ? "Impossible de se déconnecter soi-même via cette action"
                            : "Déconnecter (invalide les tokens de session)"}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          icon={<Trash2 size={14} />}
                          onClick={() => setDeleteId(u.id)}
                          disabled={isSelf}
                          className="text-red-600 dark:text-red-400"
                          title="Supprimer"
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    {search ? 'Aucun utilisateur trouvé' : 'Aucun utilisateur'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Création/Édition */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg border border-gray-200 dark:border-gray-700">
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-5">
                {editing ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
              </h3>
              <div className="space-y-4">
                {/* SearchableSelect Enseignant : uniquement à la création d'un formateur */}
                {!editing && createForm.role === 'formateur' && (
                  <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/60 rounded-lg p-3">
                    <label className="block text-sm font-medium text-amber-800 dark:text-amber-300 mb-2 flex items-center gap-1.5">
                      <Sparkles size={14} /> Sélectionner le formateur depuis la base
                    </label>
                    <SearchableSelect<EnseignantDisponibleItem>
                      value={enseignantSelectionne}
                      options={enseignantsDisponibles}
                      onChange={(ens) => {
                        if (!ens) {
                          setCreateForm({ ...createForm, enseignant_id_lien: null });
                          return;
                        }
                        // Auto-fill nom/prénoms/email + username suggéré
                        setCreateForm({
                          ...createForm,
                          enseignant_id_lien: ens.id,
                          nom: ens.nom,
                          prenoms: ens.prenoms,
                          email: ens.email,
                          username: createForm.username || suggererUsername(ens.nom, ens.prenoms),
                        });
                      }}
                      getKey={(o) => o.id}
                      getLabel={(o) => o.nom_complet}
                      getSubLabel={(o) => [o.specialite, o.email].filter(Boolean).join(' • ')}
                      getSearchText={(o) => `${o.nom_complet} ${o.email} ${o.specialite}`}
                      placeholder="— Rechercher un enseignant —"
                      clearable
                      emptyMessage="Aucun enseignant trouvé"
                      noOptionsMessage="Tous les enseignants ont déjà un compte"
                    />
                    <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-2">
                      Pour éviter les erreurs de saisie. Un mot de passe sera généré automatiquement.
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nom</label>
                    <Input
                      value={editing ? editForm.nom || '' : createForm.nom}
                      onChange={(e) => editing ? setEditForm({ ...editForm, nom: e.target.value }) : setCreateForm({ ...createForm, nom: e.target.value })}
                      disabled={!!createForm.enseignant_id_lien && !editing}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Prénoms</label>
                    <Input
                      value={editing ? editForm.prenoms || '' : createForm.prenoms}
                      onChange={(e) => editing ? setEditForm({ ...editForm, prenoms: e.target.value }) : setCreateForm({ ...createForm, prenoms: e.target.value })}
                      disabled={!!createForm.enseignant_id_lien && !editing}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nom d'utilisateur</label>
                  <Input
                    value={editing ? editForm.username || '' : createForm.username}
                    onChange={(e) => editing ? setEditForm({ ...editForm, username: e.target.value }) : setCreateForm({ ...createForm, username: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                  <Input
                    type="email"
                    value={editing ? editForm.email || '' : createForm.email}
                    onChange={(e) => editing ? setEditForm({ ...editForm, email: e.target.value }) : setCreateForm({ ...createForm, email: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rôle</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    value={editing ? editForm.role || 'agent' : createForm.role}
                    onChange={(e) => {
                      const role = e.target.value as UtilisateurCreate['role'];
                      editing ? setEditForm({ ...editForm, role }) : setCreateForm({ ...createForm, role });
                    }}
                  >
                    <option value="super_admin">Super Administrateur</option>
                    <option value="admin">Administrateur</option>
                    <option value="agent">Agent de Scolarité</option>
                    <option value="agent_caisse">Agent de Caisse</option>
                    <option value="directeur">Directeur (lecture seule)</option>
                    <option value="surveillant">Surveillant</option>
                    <option value="formateur">Formateur / Enseignant</option>
                    <option value="marketing">Marketing</option>
                    <option value="bureau_stages">Bureau des Stages</option>
                    <option value="consultation">Consultation Seule</option>
                    <option value="coordonnateur">Coordonnateur pédagogique</option>
                    <option value="invite">Invité (aide examen)</option>
                  </select>
                </div>

                {/* Pôles autorisés (uniquement pour rôles non-admin) */}
                {(() => {
                  const currentRole = editing ? (editForm.role || 'agent') : createForm.role;
                  if (currentRole === 'super_admin' || currentRole === 'admin') {
                    return (
                      <div className="text-xs text-gray-500 dark:text-gray-400 italic px-3 py-2 bg-gray-50 dark:bg-gray-700/30 rounded">
                        Les administrateurs ont accès à tous les pôles.
                      </div>
                    );
                  }
                  const currentPoles = editing ? (editForm.poles_autorises || []) : (createForm.poles_autorises || []);
                  return (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1.5">
                        <Shield size={14} className="text-blue-600" />
                        Pôles autorisés
                        <span className="text-xs font-normal text-gray-400">(vide = tous)</span>
                      </label>
                      <div className="flex flex-wrap gap-2 p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700">
                        {poles.map((p: any) => {
                          const checked = currentPoles.includes(p.nom);
                          return (
                            <label key={p.id} className={`inline-flex items-center gap-1.5 px-2 py-1 rounded cursor-pointer text-xs select-none transition-colors ${
                              checked ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-300 dark:border-blue-700' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600'
                            }`}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  const newPoles = e.target.checked
                                    ? [...currentPoles, p.nom]
                                    : currentPoles.filter(x => x !== p.nom);
                                  if (editing) setEditForm({ ...editForm, poles_autorises: newPoles });
                                  else setCreateForm({ ...createForm, poles_autorises: newPoles });
                                }}
                                className="w-3 h-3"
                              />
                              {p.nom}
                            </label>
                          );
                        })}
                        {poles.length === 0 && (
                          <span className="text-xs text-gray-400 italic">Aucun pôle disponible</span>
                        )}
                      </div>
                    </div>
                  );
                })()}
                {!editing && (
                  <>
                    {createForm.role === 'formateur' && createForm.enseignant_id_lien ? (
                      <div className="bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-800/60 rounded-lg p-3 text-xs text-emerald-700 dark:text-emerald-300 flex items-start gap-2">
                        <Sparkles size={14} className="mt-0.5 flex-shrink-0" />
                        <span>
                          Un mot de passe aléatoire sera <strong>généré automatiquement</strong> et
                          affiché une seule fois après création. À transmettre au formateur
                          (par WhatsApp, SMS, etc.) — il pourra le changer à sa première connexion.
                        </span>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mot de passe</label>
                          <Input
                            type="password"
                            value={createForm.password}
                            onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Confirmer</label>
                          <Input
                            type="password"
                            value={createForm.password2}
                            onChange={(e) => setCreateForm({ ...createForm, password2: e.target.value })}
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button variant="outline" onClick={() => setShowForm(false)}>Annuler</Button>
                <Button
                  variant="primary"
                  onClick={handleSave}
                  disabled={(() => {
                    if (creating || updating) return true;
                    if (editing) return !editForm.username;
                    // Création
                    const aEnseignant = createForm.role === 'formateur' && !!createForm.enseignant_id_lien;
                    if (aEnseignant) {
                      // username sera auto-suggéré si vide → on n'exige rien d'autre
                      return false;
                    }
                    return !createForm.username || !createForm.password;
                  })()}
                >
                  {creating || updating ? <><Loader2 size={16} className="animate-spin" /> Enregistrement...</> : editing ? 'Modifier' : 'Créer'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Mot de passe généré (affiché une seule fois) */}
      {mdpGenere && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-700">
            <div className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles size={20} className="text-amber-500" />
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  Compte formateur créé
                </h3>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Voici les identifiants pour <strong>{mdpGenere.nomComplet}</strong>.
                Notez-les ou copiez-les maintenant — le mot de passe ne sera plus affiché.
              </p>

              <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-3 mb-3 font-mono text-sm space-y-2">
                <div>
                  <div className="text-[10px] uppercase text-gray-500 mb-0.5">Identifiant</div>
                  <div className="text-gray-900 dark:text-gray-100 select-all">{mdpGenere.username}</div>
                </div>
                <div className="border-t border-gray-200 dark:border-gray-700 pt-2">
                  <div className="text-[10px] uppercase text-gray-500 mb-0.5">Mot de passe</div>
                  <div className="text-gray-900 dark:text-gray-100 select-all tracking-wider">{mdpGenere.password}</div>
                </div>
              </div>

              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/60 rounded-lg p-3 text-xs text-amber-700 dark:text-amber-300 mb-4">
                Le formateur devra changer ce mot de passe à sa première connexion.
                Transmettez-le par WhatsApp, SMS ou en personne — pas par email.
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={copierMdp}
                  icon={mdpCopie ? <Check size={14} /> : <Copy size={14} />}>
                  {mdpCopie ? 'Copié !' : 'Copier'}
                </Button>
                <Button variant="primary" onClick={() => { setMdpGenere(null); setCreateForm(EMPTY_CREATE); }}>
                  Terminé
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Suppression */}
      {deleteId !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-sm border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-3">Supprimer cet utilisateur ?</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">Cette action est irréversible. Toutes les données associées seront perdues.</p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setDeleteId(null)}>Annuler</Button>
              <Button variant="danger" onClick={handleDelete}>Supprimer</Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Gestion des Pôles */}
      {polesUserId !== null && (() => {
        const user = utilisateurs?.find(u => u.id === polesUserId);
        if (!user) return null;
        const isAdmin = user.role === 'super_admin' || user.role === 'admin';
        return (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <Shield size={20} className="text-blue-500" />
                    Gérer les pôles autorisés
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {user.nom} {user.prenoms} ({user.username}) — {ROLE_LABELS[user.role]}
                  </p>
                </div>
                <button onClick={() => setPolesUserId(null)} className="text-gray-400 hover:text-gray-600">
                  <X size={20} />
                </button>
              </div>

              <div className="p-6 space-y-5">
                {isAdmin ? (
                  <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      Cet utilisateur est <strong>{ROLE_LABELS[user.role]}</strong> et a automatiquement accès à <strong>tous les pôles</strong>.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Pôles permanents */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Pôles autorisés (accès permanent)
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {poles.map((p: any) => {
                          const checked = polesSelected.includes(p.nom);
                          return (
                            <label key={p.id} className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-sm select-none transition-colors ${
                              checked ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-2 border-blue-400 dark:border-blue-600' : 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-2 border-gray-200 dark:border-gray-600'
                            }`}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  setPolesSelected(e.target.checked
                                    ? [...polesSelected, p.nom]
                                    : polesSelected.filter(x => x !== p.nom));
                                }}
                                className="w-4 h-4"
                              />
                              {p.nom}
                            </label>
                          );
                        })}
                      </div>
                      <p className="text-[11px] text-gray-400 mt-2">
                        {polesSelected.length === 0
                          ? '⚠ Aucun pôle sélectionné = accès à tous les pôles (par défaut)'
                          : `L'utilisateur ne verra que les données des ${polesSelected.length} pôle(s) sélectionné(s)`}
                      </p>
                      <Button
                        variant="primary"
                        className="mt-3"
                        disabled={affectingPoles}
                        onClick={async () => {
                          try {
                            await affecterPoles({ id: polesUserId, poles: polesSelected }).unwrap();
                            showMsg('success', 'Pôles mis à jour');
                          } catch (err: any) {
                            showMsg('error', err.data?.error || 'Erreur');
                          }
                        }}
                      >
                        {affectingPoles ? 'Enregistrement...' : 'Enregistrer les pôles'}
                      </Button>
                    </div>

                    {/* Accès temporaires existants */}
                    {(user.poles_acces_temporaire && user.poles_acces_temporaire.length > 0) && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                          <Clock size={14} className="text-amber-500" />
                          Accès temporaires en cours
                        </label>
                        <div className="space-y-2">
                          {user.poles_acces_temporaire.map(a => {
                            const expired = new Date(a.date_expiration) < new Date();
                            return (
                              <div key={a.pole} className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg border ${
                                expired ? 'bg-gray-50 dark:bg-gray-700/30 border-gray-200 dark:border-gray-600 opacity-60'
                                : 'bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800'
                              }`}>
                                <div className="flex-1">
                                  <span className="font-medium text-sm text-gray-900 dark:text-gray-100">{a.pole}</span>
                                  <span className="text-xs text-gray-500 ml-2">
                                    {expired ? 'Expiré le' : 'Jusqu\'au'} {new Date(a.date_expiration).toLocaleDateString('fr-FR')}
                                  </span>
                                  {a.accorde_par && (
                                    <div className="text-[10px] text-gray-400">Accordé par {a.accorde_par}</div>
                                  )}
                                </div>
                                <button
                                  onClick={async () => {
                                    try {
                                      await revoquerAcces({ id: polesUserId, pole: a.pole }).unwrap();
                                      showMsg('success', 'Accès révoqué');
                                    } catch (err: any) {
                                      showMsg('error', err.data?.error || 'Erreur');
                                    }
                                  }}
                                  className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                                  title="Révoquer"
                                >
                                  <X size={14} />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Accorder un accès temporaire */}
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                        <Clock size={14} className="text-amber-500" />
                        Accorder un accès temporaire à un pôle
                      </label>
                      <div className="flex gap-2">
                        <select
                          value={tempAccesPole}
                          onChange={(e) => setTempAccesPole(e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                        >
                          <option value="">— Pôle —</option>
                          {poles
                            .filter((p: any) => !polesSelected.includes(p.nom))
                            .map((p: any) => (
                              <option key={p.id} value={p.nom}>{p.nom}</option>
                            ))}
                        </select>
                        <input
                          type="date"
                          value={tempAccesDate}
                          onChange={(e) => setTempAccesDate(e.target.value)}
                          min={new Date().toISOString().split('T')[0]}
                          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                        />
                        <Button
                          variant="primary"
                          disabled={!tempAccesPole || !tempAccesDate}
                          onClick={async () => {
                            try {
                              await accorderAcces({ id: polesUserId, pole: tempAccesPole, date_expiration: tempAccesDate }).unwrap();
                              showMsg('success', 'Accès temporaire accordé');
                              setTempAccesPole('');
                              setTempAccesDate('');
                            } catch (err: any) {
                              showMsg('error', err.data?.error || 'Erreur');
                            }
                          }}
                        >
                          Accorder
                        </Button>
                      </div>
                      <p className="text-[10px] text-gray-400 mt-1">
                        Pour cas particulier — l'accès sera révoqué automatiquement à la date d'expiration
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal Reset Password */}
      {resetUserId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2 mb-4">
              <KeyRound size={20} className="text-purple-500" />
              Réinitialiser le mot de passe
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Utilisateur : <span className="font-medium text-gray-800 dark:text-gray-200">
                {utilisateurs?.find(u => u.id === resetUserId)?.username}
              </span>
            </p>
            <input
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder="Nouveau mot de passe (min. 8 caractères)"
              className="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 mb-4"
            />
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setResetUserId(null)}>Annuler</Button>
              <Button
                variant="primary"
                disabled={newPassword.length < 6}
                onClick={async () => {
                  try {
                    const res = await resetPassword({ id: resetUserId, new_password: newPassword }).unwrap();
                    showMsg('success', res.message);
                    setResetUserId(null);
                  } catch (err: any) {
                    showMsg('error', err.data?.error || 'Erreur');
                  }
                }}
              >
                Réinitialiser
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UtilisateursTab;
